import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import Nora from '@primeuix/themes/nora';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import App from './App.vue';
import router from './router';
import { initialiseMsal } from './auth/msal';
import 'primeicons/primeicons.css';
import './styles.css';

async function bootstrap() {
  await initialiseMsal();

  const app = createApp(App);
  app.use(createPinia());
  app.use(router);
  // PrimeVue 4 styled mode. Nora preset gives a more conservative, formal admin look; we
  // disable the dark-mode auto-selector for now (no toggle in the UI yet — easy follow-up).
  // `cssLayer` keeps PrimeVue's component CSS in a named layer so our scoped styles win
  // without needing !important overrides.
  app.use(PrimeVue, {
    theme: {
      preset: Nora,
      options: {
        // Class-based dark switching. The theme store (stores/theme.ts) toggles `.p-dark`
        // on `document.documentElement` based on the operator's persisted preference plus
        // the system `prefers-color-scheme` query when preference is `system`.
        darkModeSelector: '.p-dark',
        cssLayer: {
          name: 'primevue',
          order: 'app, primevue',
        },
      },
    },
  });
  app.use(ConfirmationService);
  app.use(ToastService);

  // Bootstrap theme before mount so there's no flash of unstyled content. The store reads
  // localStorage, sets up the prefers-color-scheme listener, and applies `.p-dark` to the
  // root element if dark is currently resolved.
  const { useThemeStore } = await import('./stores/theme');
  useThemeStore().bootstrap();

  // Register realtime event handlers before AppShell triggers the SignalR handshake. The
  // installer is idempotent; AppShell.onMounted calls startRealtime() to open the socket.
  const { installRealtimeSubscriptions } = await import('./lib/realtimeSubscriptions');
  installRealtimeSubscriptions();

  app.mount('#app');
}

bootstrap();
